/**
 * Gallery Layout Strategy
 *
 * Gallery mode displays one dimension (run, step, or index) as rows,
 * with other dimensions controlled via sliders in the header/footer.
 *
 * This file delegates to axis-specific strategies for implementation.
 */

import {MediaBrowserPanelConfig} from '../common';
import {getGalleryAxisStrategy} from './gallery';
import {LayoutStrategy} from './LayoutStrategy';
import {GridData, SlotRegistry, TransformInput} from './types';

export const GalleryLayout: LayoutStrategy = {
  id: 'gallery',

  transform(input: TransformInput): GridData {
    const {config} = input;
    const {gallerySettings, mediaKeys} = config;
    const axis = gallerySettings?.axis ?? 'run';

    if (!mediaKeys || mediaKeys.length === 0) {
      return {rows: [], metadata: {layoutType: 'gallery', totalCells: 0}};
    }

    const strategy = getGalleryAxisStrategy(axis);
    const axisValues = strategy.getAxisValues(input, config.maxGalleryItems);
    const rows = strategy.createRows(input, axisValues);

    const totalCells = rows.reduce((sum, row) => sum + row.cells.length, 0);

    return {
      rows,
      metadata: {
        layoutType: 'gallery',
        totalCells,
        axisData: {
          x: [0], // Single column in gallery
          // Filter out null values from axis values to match old behavior
          y: axisValues.filter((v): v is number => v != null),
        },
      },
    };
  },

  buildSlotRegistry(config: MediaBrowserPanelConfig): SlotRegistry {
    const axis = config.gallerySettings?.axis ?? 'run';

    const strategy = getGalleryAxisStrategy(axis);
    return strategy.buildSlotRegistry();
  },
};
